import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import BBCodeEditor from '../../../components/BBCodeEditor/BBCodeEditor'
import PortraitCropModal from '../../../components/PortraitCropModal/PortraitCropModal'
import { parseBBCode } from '../../../libraries/BBCode'
import HttpClient from '../../../services/HttpClient'
import { getCharacterPortraitCandidates, SHURIKEN_PORTRAIT } from '../../../utils/characterPortrait'
import './MissionIssuerNpcSheet.css'

const PORTRAIT_ACCEPT = 'image/png,image/jpeg,image/webp'
const PORTRAIT_MAX_BYTES = 5 * 1024 * 1024


const STATUS_LABELS = {
  Active: 'Activa',
  PendingIssuer: 'Pendiente ficha',
  InProgress: 'En curso',
  Completed: 'Completada',
  Failed: 'Fallida',
  Aborted: 'Abortada',
  Archived: 'Archivada',
}

const EMPTY_PSYCH = {
  personality: '',
  strengths: '',
  weaknesses: '',
  fears: '',
  desires: '',
  catchphrase: '',
  vibe: '',
}

const PSYCH_FIELDS = [
  { key: 'personality', label: 'Personalidad', rows: 3, placeholder: 'Cómo se comporta, tono, trato…', wide: true },
  { key: 'strengths', label: 'Fortalezas', rows: 2, placeholder: 'En qué destaca' },
  { key: 'weaknesses', label: 'Debilidades', rows: 2, placeholder: 'Sus puntos flacos' },
  { key: 'fears', label: 'Miedos', rows: 2, placeholder: 'Qué le inquieta o evita' },
  { key: 'desires', label: 'Deseos', rows: 2, placeholder: 'Qué busca o anhela' },
  { key: 'catchphrase', label: 'Frase característica', rows: 1, placeholder: 'Una línea que lo define', wide: true },
  { key: 'vibe', label: 'Vibra general', rows: 2, placeholder: 'Sensación al encontrarlo', wide: true },
]

function normalizePsych(raw) {
  const next = { ...EMPTY_PSYCH }
  if (!raw || typeof raw !== 'object') return next
  for (const key of Object.keys(EMPTY_PSYCH)) {
    if (typeof raw[key] === 'string') next[key] = raw[key]
  }
  return next
}

function newPhysicalId() {
  return `phys_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
}

function normalizePhysical(raw) {
  if (!Array.isArray(raw)) return []
  return raw
    .filter((item) => item && typeof item === 'object')
    .map((item) => ({
      id: String(item.id || newPhysicalId()),
      label: typeof item.label === 'string' ? item.label : '',
      text: typeof item.text === 'string' ? item.text : '',
    }))
}

function SectionHeading({ children }) {
  return (
    <header className="mins-sheet__section-head">
      <span className="mins-sheet__ornament" aria-hidden="true" />
      <h2>{children}</h2>
      <span className="mins-sheet__ornament mins-sheet__ornament--right" aria-hidden="true" />
    </header>
  )
}

export default function MissionIssuerNpcSheet({ funcNav = () => {}, funcCharNavbar = () => {} }) {
  const { slug } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [sheet, setSheet] = useState(null)
  const [hans, setHans] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [editing, setEditing] = useState(searchParams.get('edit') === '1')
  const [form, setForm] = useState({
    name: '',
    age: '',
    hanSlug: '',
    occupation: '',
    residence: '',
    status: '',
    clan: '',
    physical: [],
    psych: { ...EMPTY_PSYCH },
    history: '',
  })

  const wantEdit = searchParams.get('edit') === '1'

  useEffect(() => {
    // Alinear con AppRoutes (ruta /npc): sin Navbar global ni CharNavbar.
    // funcNav(true) pelea con isDetail y remonta AppRoutes en bucle.
    if (typeof funcNav === 'function') funcNav(false)
    if (typeof funcCharNavbar === 'function') funcCharNavbar(false)
  }, [funcNav, funcCharNavbar])

  const applySheetToForm = useCallback((data) => {
    setForm({
      name: data?.name || '',
      age: data?.age != null ? String(data.age) : '',
      hanSlug: data?.hanSlug || '',
      occupation: data?.occupation || '',
      residence: data?.residence || '',
      status: data?.status || '',
      clan: data?.clan || data?.clanName || '',
      physical: normalizePhysical(data?.physical),
      psych: normalizePsych(data?.psych),
      history: data?.history || '',
    })
  }, [])

  useEffect(() => {
    let cancelled = false
    if (!slug) {
      setLoading(false)
      return undefined
    }

    setLoading(true)
    ;(async () => {
      try {
        const [{ data }, hansRes] = await Promise.all([
          HttpClient().get(`/api/v1/characters/${encodeURIComponent(slug)}/npc-sheet`),
          HttpClient().get('/api/v1/travel/playable-hans').catch(() => ({ data: null })),
        ])
        if (cancelled) return
        setSheet(data)
        applySheetToForm(data)
        const missions = Array.isArray(data?.issuedMissions) ? data.issuedMissions : []
        const preferred =
          missions.find((m) => m.status === 'Active' || m.status === 'InProgress') || missions[0]
        setOpenMissionId(preferred?.id != null ? Number(preferred.id) : null)
        const list = Array.isArray(hansRes?.data?.hans)
          ? hansRes.data.hans
          : Array.isArray(hansRes?.data)
            ? hansRes.data
            : []
        setHans(list)
        if (wantEdit && data?.canEdit) {
          setEditing(true)
        }
      } catch (e) {
        if (cancelled) return
        const code = e?.response?.data?.code || e?.code
        if (code === 'NOT_MISSION_ISSUER' || code === 'NOT_LIGHT_NPC') {
          navigate(`/personajes/${slug}/ficha`, { replace: true })
          return
        }
        toast.error(e?.response?.data?.error || e?.message || 'No se pudo cargar la ficha')
        setSheet(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [slug, wantEdit]) // navigate/applySheetToForm estables; no re-disparar carga

  const [portraitIdx, setPortraitIdx] = useState(0)
  const [portraitBust, setPortraitBust] = useState(0)
  const [cropSrc, setCropSrc] = useState(null)
  const [openMissionId, setOpenMissionId] = useState(null)
  const fileInputRef = useRef(null)

  const portraitCandidates = useMemo(
    () => getCharacterPortraitCandidates({ slug: sheet?.slug, name: sheet?.name }),
    [sheet?.slug, sheet?.name]
  )

  useEffect(() => {
    setPortraitIdx(0)
  }, [sheet?.slug, sheet?.name, portraitBust])

  useEffect(() => {
    return () => {
      if (cropSrc) URL.revokeObjectURL(cropSrc)
    }
  }, [cropSrc])

  const portraitSrcRaw = portraitCandidates[portraitIdx] || SHURIKEN_PORTRAIT
  const portraitSrc =
    portraitBust && portraitSrcRaw !== SHURIKEN_PORTRAIT
      ? `${portraitSrcRaw}${portraitSrcRaw.includes('?') ? '&' : '?'}t=${portraitBust}`
      : portraitSrcRaw

  const openPortraitPicker = () => {
    if (!editing || !sheet?.canEdit) return
    fileInputRef.current?.click()
  }

  const handlePortraitFile = (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (!/^image\/(png|jpeg|webp)$/.test(file.type)) {
      toast.error('Usa una imagen PNG, JPEG o WebP')
      return
    }
    if (file.size > PORTRAIT_MAX_BYTES) {
      toast.error('La imagen supera el límite de 5 MB')
      return
    }
    if (cropSrc) URL.revokeObjectURL(cropSrc)
    setCropSrc(URL.createObjectURL(file))
  }

  const handleCropCancel = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc)
    setCropSrc(null)
  }

  const handleCropConfirm = async ({ full, sm }) => {
    if (!slug) return
    const formData = new FormData()
    formData.append('portrait', full, `${slug}.png`)
    formData.append('portraitSm', sm, `${slug}-sm.png`)
    try {
      await HttpClient().post(
        `/api/v1/characters/${encodeURIComponent(slug)}/portrait`,
        formData,
        {
          headers: { 'Content-Type': undefined },
          timeout: 60000,
        }
      )
      if (cropSrc) URL.revokeObjectURL(cropSrc)
      setCropSrc(null)
      setPortraitIdx(0)
      setPortraitBust(Date.now())
      toast.success('Retrato actualizado')
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.message || 'No se pudo subir el retrato')
      throw err
    }
  }

  const descPhysicalHtml = useMemo(
    () => parseBBCode(sheet?.descPhysical || ''),
    [sheet?.descPhysical]
  )

  const viewPhysical = useMemo(
    () => normalizePhysical(sheet?.physical),
    [sheet?.physical]
  )
  const hasViewPhysical = viewPhysical.some((s) => String(s.text || '').trim())

  const historyHtml = useMemo(() => parseBBCode(sheet?.history || ''), [sheet?.history])

  const setPsychField = (key, value) => {
    setForm((f) => ({ ...f, psych: { ...f.psych, [key]: value } }))
  }

  const addPhysicalSection = () => {
    setForm((f) => ({
      ...f,
      physical: [...f.physical, { id: newPhysicalId(), label: '', text: '' }],
    }))
  }

  const updatePhysicalSection = (id, patch) => {
    setForm((f) => ({
      ...f,
      physical: f.physical.map((s) => (s.id === id ? { ...s, ...patch } : s)),
    }))
  }

  const removePhysicalSection = (id) => {
    setForm((f) => ({
      ...f,
      physical: f.physical.filter((s) => s.id !== id),
    }))
  }

  const handleSave = async () => {
    if (!slug || !sheet?.canEdit) return
    setSaving(true)
    try {
      const { data } = await HttpClient().patch(
        `/api/v1/characters/${encodeURIComponent(slug)}/npc-sheet`,
        {
          name: form.name,
          age: form.age === '' ? null : Number(form.age),
          hanSlug: form.hanSlug || null,
          occupation: form.occupation,
          residence: form.residence,
          status: form.status,
          clan: form.clan,
          physical: form.physical,
          psych: form.psych,
          history: form.history,
        }
      )
      setSheet(data)
      applySheetToForm(data)
      setEditing(false)
      toast.success('Ficha guardada')
      if (data?.slug && data.slug !== slug) {
        navigate(`/personajes/${data.slug}/npc`, { replace: true })
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.message || 'No se pudo guardar')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="mins-page parchment-scope">
        <div className="mins-sheet mins-sheet--plain">
          <p className="mins-sheet__loading">Cargando ficha…</p>
        </div>
      </div>
    )
  }

  if (!sheet) {
    return (
      <div className="mins-page parchment-scope">
        <div className="mins-sheet mins-sheet--plain">
          <p className="mins-sheet__loading">No se encontró la ficha.</p>
          <Link
            to={
              typeof location.state?.from === 'string' && location.state.from.startsWith('/')
                ? location.state.from
                : '/'
            }
            className="mins-sheet__link"
          >
            Volver
          </Link>
        </div>
      </div>
    )
  }

  const viewPsych = normalizePsych(sheet.psych)
  const catchphrase = String(viewPsych.catchphrase || '').trim()
  const vibe = String(viewPsych.vibe || '').trim()
  const bodyPsychFields = PSYCH_FIELDS.filter(
    (f) => f.key !== 'catchphrase' && f.key !== 'vibe'
  )
  const hasBodyPsych = bodyPsychFields.some((f) => String(viewPsych[f.key] || '').trim())

  const returnParam = searchParams.get('return')
  const returnFromQuery =
    typeof returnParam === 'string' &&
    returnParam.startsWith('/') &&
    !returnParam.startsWith('//')
      ? returnParam
      : null
  const fromPath =
    (typeof location.state?.from === 'string' && location.state.from.startsWith('/')
      ? location.state.from
      : null) || returnFromQuery
  const territoryPath = sheet.hanSlug ? `/han/${sheet.hanSlug}` : null
  const postAsId = searchParams.get('postAs')
  const backTo = (() => {
    if (!fromPath) return territoryPath || '/'
    if (postAsId && Number(postAsId) > 0) {
      const sep = fromPath.includes('?') ? '&' : '?'
      return `${fromPath}${sep}postAs=${encodeURIComponent(postAsId)}`
    }
    return fromPath
  })()
  const backLabel = fromPath
    ? 'Volver'
    : sheet.hanName
      ? `Volver a ${sheet.hanName}`
      : territoryPath
        ? 'Volver al territorio'
        : 'Volver al inicio'

  return (
    <div className="mins-page parchment-scope">
      {cropSrc ? (
        <PortraitCropModal
          imageSrc={cropSrc}
          onCancel={handleCropCancel}
          onConfirm={handleCropConfirm}
        />
      ) : null}
      <input
        ref={fileInputRef}
        type="file"
        accept={PORTRAIT_ACCEPT}
        className="mins-sheet__file-input"
        onChange={handlePortraitFile}
        tabIndex={-1}
        aria-hidden="true"
      />
      <div className="mins-sheet__nav">
        <Link to={backTo} className="mins-sheet__back" state={fromPath ? location.state : undefined}>
          <span aria-hidden="true">←</span> {backLabel}
        </Link>
      </div>
      <article className={`mins-sheet${editing ? ' mins-sheet--editing' : ''}`}>
        <div className="mins-sheet__binding" aria-hidden="true" />

        <header className="mins-sheet__masthead">
          <div className="mins-sheet__mast-top">
            <div className="mins-sheet__portrait-wrap">
              <div
                className={`mins-sheet__portrait${editing && sheet.canEdit ? ' mins-sheet__portrait--editable' : ''}`}
              >
                <img
                  key={portraitSrc}
                  src={portraitSrc}
                  alt=""
                  onError={() => {
                    setPortraitIdx((i) => Math.min(i + 1, Math.max(portraitCandidates.length - 1, 0)))
                  }}
                />
                {editing && sheet.canEdit ? (
                  <button
                    type="button"
                    className="mins-sheet__portrait-edit"
                    onClick={openPortraitPicker}
                  >
                    Cambiar retrato
                  </button>
                ) : null}
              </div>
            </div>

            <div className="mins-sheet__identity">
              <div className="mins-sheet__title-row">
                <div className="mins-sheet__title-block">
                  <p className="mins-sheet__kicker">Emisor de misión</p>
                  {editing ? (
                    <input
                      className="mins-sheet__input mins-sheet__input--title"
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      maxLength={80}
                      aria-label="Nombre"
                    />
                  ) : (
                    <h1 className="mins-sheet__name">{sheet.name}</h1>
                  )}
                </div>
                <div className="mins-sheet__actions">
                  {sheet.canEdit && !editing ? (
                    <button type="button" className="mins-sheet__btn" onClick={() => setEditing(true)}>
                      Editar
                    </button>
                  ) : null}
                  {sheet.canEdit && editing ? (
                    <>
                      <button
                        type="button"
                        className="mins-sheet__btn mins-sheet__btn--ghost"
                        disabled={saving}
                        onClick={() => {
                          setEditing(false)
                          applySheetToForm(sheet)
                        }}
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        className="mins-sheet__btn mins-sheet__btn--primary"
                        disabled={saving}
                        onClick={handleSave}
                      >
                        {saving ? 'Guardando…' : 'Guardar'}
                      </button>
                    </>
                  ) : null}
                </div>
              </div>

              {editing ? (
                <div className="mins-sheet__byline mins-sheet__byline--edit">
                  <label className="mins-sheet__byline-field">
                    <span>Ocupación</span>
                    <input
                      className="mins-sheet__input"
                      value={form.occupation}
                      onChange={(e) => setForm((f) => ({ ...f, occupation: e.target.value }))}
                      maxLength={120}
                      placeholder="Oficio, cargo o rol público"
                    />
                  </label>
                  <label className="mins-sheet__byline-field">
                    <span>Residencia</span>
                    <input
                      className="mins-sheet__input"
                      value={form.residence}
                      onChange={(e) => setForm((f) => ({ ...f, residence: e.target.value }))}
                      maxLength={120}
                      placeholder="Lugar donde vive o opera"
                    />
                  </label>
                </div>
              ) : (
                <div className="mins-sheet__byline">
                  {sheet.occupation ? (
                    <p className="mins-sheet__occupation">{sheet.occupation}</p>
                  ) : null}
                  {sheet.residence ? (
                    <p className="mins-sheet__residence">{sheet.residence}</p>
                  ) : null}
                </div>
              )}

              {!editing && (catchphrase || vibe) ? (
                <div className="mins-sheet__lead">
                  {catchphrase ? <p className="mins-sheet__catchphrase">“{catchphrase}”</p> : null}
                  {vibe ? <p className="mins-sheet__vibe">{vibe}</p> : null}
                </div>
              ) : null}
            </div>
          </div>

          <div className={`mins-sheet__registry${editing ? ' mins-sheet__registry--edit' : ''}`}>
            {editing ? (
              <>
                <label className="mins-sheet__reg-field">
                  <span>Edad</span>
                  <input
                    className="mins-sheet__input mins-sheet__input--sm"
                    type="number"
                    min={0}
                    max={200}
                    value={form.age}
                    onChange={(e) => setForm((f) => ({ ...f, age: e.target.value }))}
                  />
                </label>
                <label className="mins-sheet__reg-field">
                  <span>Estado</span>
                  <input
                    className="mins-sheet__input"
                    value={form.status}
                    onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                    maxLength={120}
                    placeholder="Activo…"
                  />
                </label>
                <label className="mins-sheet__reg-field">
                  <span>Clan</span>
                  <input
                    className="mins-sheet__input"
                    value={form.clan}
                    onChange={(e) => setForm((f) => ({ ...f, clan: e.target.value }))}
                    maxLength={120}
                    placeholder="Linaje"
                  />
                </label>
                <label className="mins-sheet__reg-field">
                  <span>Han</span>
                  <select
                    className="mins-sheet__input"
                    value={form.hanSlug}
                    onChange={(e) => setForm((f) => ({ ...f, hanSlug: e.target.value }))}
                  >
                    <option value="">—</option>
                    {hans.map((h) => (
                      <option key={h.slug || h.territoryId} value={h.slug}>
                        {h.name || h.slug}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            ) : (
              <>
                <div className="mins-sheet__reg-item">
                  <span className="mins-sheet__reg-label">Edad</span>
                  <span className="mins-sheet__reg-value">
                    {sheet.age != null ? sheet.age : '—'}
                  </span>
                </div>
                <div className="mins-sheet__reg-item">
                  <span className="mins-sheet__reg-label">Estado</span>
                  <span className="mins-sheet__reg-value">{sheet.status || '—'}</span>
                </div>
                <div className="mins-sheet__reg-item">
                  <span className="mins-sheet__reg-label">Clan</span>
                  <span className="mins-sheet__reg-value">
                    {sheet.clan || sheet.clanName || '—'}
                  </span>
                </div>
                <div className="mins-sheet__reg-item">
                  <span className="mins-sheet__reg-label">Han</span>
                  <span className="mins-sheet__reg-value">
                    {sheet.hanName || sheet.hanSlug || 'Sin Han'}
                  </span>
                </div>
              </>
            )}
          </div>
        </header>

        <div className="mins-sheet__body">
          <section className="mins-sheet__block">
            <SectionHeading>Descripción física</SectionHeading>
            {editing ? (
              <div className="mins-sheet__physical-edit">
                {form.physical.map((section) => (
                  <div key={section.id} className="mins-sheet__physical-field">
                    <div className="mins-sheet__physical-field-head">
                      <label className="mins-sheet__physical-label-wrap">
                        <span className="mins-sheet__psych-label">Apartado</span>
                        <input
                          className="mins-sheet__input"
                          value={section.label}
                          onChange={(e) =>
                            updatePhysicalSection(section.id, { label: e.target.value })
                          }
                          placeholder="Ej. Ropa, Cicatrices, Estatura…"
                          maxLength={80}
                          aria-label="Nombre del apartado"
                        />
                      </label>
                      <button
                        type="button"
                        className="mins-sheet__btn mins-sheet__btn--ghost mins-sheet__physical-remove"
                        onClick={() => removePhysicalSection(section.id)}
                      >
                        Quitar
                      </button>
                    </div>
                    <label className="mins-sheet__physical-text-wrap">
                      <span className="mins-sheet__psych-label">Descripción</span>
                      <BBCodeEditor
                        value={section.text}
                        onChange={(value) => updatePhysicalSection(section.id, { text: value })}
                        height="140px"
                        enableRPCode={false}
                        placeholder="Descripción de este apartado. Puedes usar BBCode."
                      />
                    </label>
                  </div>
                ))}
                <button
                  type="button"
                  className="mins-sheet__btn mins-sheet__btn--ghost mins-sheet__physical-add"
                  onClick={addPhysicalSection}
                >
                  Añadir apartado
                </button>
                {form.physical.length === 0 ? (
                  <p className="mins-sheet__prose mins-sheet__prose--empty">
                    Aún no hay apartados. Añade uno (p. ej. Ropa, Aspecto general).
                  </p>
                ) : null}
              </div>
            ) : hasViewPhysical ? (
              <dl className="mins-sheet__psych-view">
                {viewPhysical.map((section) => {
                  const label = String(section.label || '').trim() || 'Apartado'
                  const text = String(section.text || '').trim()
                  if (!text) return null
                  return (
                    <div key={section.id} className="mins-sheet__psych-item mins-sheet__psych-item--wide">
                      <dt>{label}</dt>
                      <dd
                        className="mins-sheet__bbcode"
                        dangerouslySetInnerHTML={{ __html: parseBBCode(section.text) }}
                      />
                    </div>
                  )
                })}
              </dl>
            ) : sheet.descPhysical ? (
              <div
                className="mins-sheet__bbcode"
                dangerouslySetInnerHTML={{ __html: descPhysicalHtml }}
              />
            ) : (
              <p className="mins-sheet__prose mins-sheet__prose--empty">Sin descripción.</p>
            )}
          </section>

          <section className="mins-sheet__block">
            <SectionHeading>Perfil psicológico</SectionHeading>
            {editing ? (
              <div className="mins-sheet__psych-grid">
                {PSYCH_FIELDS.map((field) => (
                  <label
                    key={field.key}
                    className={`mins-sheet__psych-field${field.wide ? ' mins-sheet__psych-field--wide' : ''}`}
                  >
                    <span className="mins-sheet__psych-label">{field.label}</span>
                    {field.rows <= 1 ? (
                      <input
                        className="mins-sheet__input"
                        value={form.psych[field.key] || ''}
                        onChange={(e) => setPsychField(field.key, e.target.value)}
                        placeholder={field.placeholder}
                        maxLength={field.key === 'catchphrase' ? 160 : undefined}
                      />
                    ) : (
                      <textarea
                        className="mins-sheet__textarea"
                        rows={field.rows}
                        value={form.psych[field.key] || ''}
                        onChange={(e) => setPsychField(field.key, e.target.value)}
                        placeholder={field.placeholder}
                      />
                    )}
                  </label>
                ))}
              </div>
            ) : hasBodyPsych ? (
              <dl className="mins-sheet__psych-view">
                {bodyPsychFields.map((field) => {
                  const value = String(viewPsych[field.key] || '').trim()
                  if (!value) return null
                  return (
                    <div
                      key={field.key}
                      className={`mins-sheet__psych-item${
                        field.wide ? ' mins-sheet__psych-item--wide' : ''
                      }`}
                    >
                      <dt>{field.label}</dt>
                      <dd>{value}</dd>
                    </div>
                  )
                })}
              </dl>
            ) : (
              <p className="mins-sheet__prose mins-sheet__prose--empty">
                {catchphrase || vibe
                  ? 'El sello del emisor está en el encabezado.'
                  : 'Sin perfil psicológico.'}
              </p>
            )}
          </section>

          <section className="mins-sheet__block">
            <SectionHeading>Historia</SectionHeading>
            {editing ? (
              <BBCodeEditor
                value={form.history}
                onChange={(value) => setForm((f) => ({ ...f, history: value }))}
                height="260px"
                enableRPCode={false}
                placeholder="Historia del emisor. Puedes usar BBCode."
              />
            ) : sheet.history ? (
              <div className="mins-sheet__bbcode" dangerouslySetInnerHTML={{ __html: historyHtml }} />
            ) : (
              <p className="mins-sheet__prose mins-sheet__prose--empty">Sin historia.</p>
            )}
          </section>

          {sheet.isMissionIssuer ? (
          <section className="mins-sheet__block mins-sheet__block--missions">
            <SectionHeading>Misiones emitidas</SectionHeading>
            {Array.isArray(sheet.issuedMissions) && sheet.issuedMissions.length > 0 ? (
              <ul className="mins-sheet__notebook">
                {sheet.issuedMissions.map((m) => {
                  const mid = Number(m.id)
                  const isOpen = openMissionId === mid
                  const synopsis = String(m.description || '').trim()
                  return (
                    <li
                      key={mid}
                      className={`mins-sheet__notebook-page${isOpen ? ' mins-sheet__notebook-page--open' : ''}`}
                    >
                      <button
                        type="button"
                        className="mins-sheet__notebook-tab"
                        aria-expanded={isOpen}
                        onClick={() => setOpenMissionId(isOpen ? null : mid)}
                      >
                        <span className="mins-sheet__notebook-tab-main">
                          {m.rank ? <span className="mins-sheet__rank">{m.rank}</span> : null}
                          <span className="mins-sheet__mission-title">
                            {m.title || `Misión #${mid}`}
                          </span>
                        </span>
                        <span className="mins-sheet__notebook-tab-side">
                          <span className="mins-sheet__mission-stamp">
                            {STATUS_LABELS[m.status] || m.status || '—'}
                          </span>
                          <span className="mins-sheet__notebook-chevron" aria-hidden="true" />
                        </span>
                      </button>
                      {isOpen ? (
                        <div className="mins-sheet__notebook-leaf">
                          <p className="mins-sheet__notebook-kicker">Aviso del tablón</p>
                          {synopsis ? (
                            <div
                              className="mins-sheet__notebook-body mins-sheet__bbcode"
                              dangerouslySetInnerHTML={{ __html: parseBBCode(synopsis) }}
                            />
                          ) : (
                            <p className="mins-sheet__prose mins-sheet__prose--empty">
                              Esta misión aún no tiene sinopsis.
                            </p>
                          )}
                        </div>
                      ) : null}
                    </li>
                  )
                })}
              </ul>
            ) : (
              <p className="mins-sheet__prose mins-sheet__prose--empty">
                Aún no hay misiones vinculadas a este emisor.
              </p>
            )}
          </section>
          ) : null}
        </div>
      </article>
    </div>
  )
}
